import { BUSINESS_TYPES, DEFAULT_GEOGRAPHY, GOALS } from './onboardingConfig';
import { DENTIST_INDUSTRY_VALUE, canonicalizeIndustryValue } from '../industryCanonicalization';

const FALLBACK_BUSINESS = BUSINESS_TYPES[0];
const FALLBACK_GOAL = GOALS[0];
const SIGNAL_FILTERS_ENABLED = false;
const COMPANY_SIZE_FILTERS_ENABLED = false;

const PEOPLE_INTENT_REGEX = /\b(people|person|contact|contacts|founder|co-founder|owner|ceo|cmo|manager|director|vp|head|title|titles|role|roles|email)\b/;
const COMPANY_INTENT_REGEX = /\b(company|companies|business|businesses|clinic|clinics|agency|agencies|shop|shops|firm|firms|dentist|dentists)\b/;
const HIRING_INTENT_REGEX = /\b(hiring|recruiting|expanding)\b/;

const ROLE_HINTS = [
  {
    pattern: /\b(marketing managers?|marketing leads?|head of marketing|vp marketing|director of marketing)\b/,
    facetLabel: 'Marketing managers and leaders',
    roleFilter: 'marketing',
    seniorityFilter: 'Manager',
  },
  {
    pattern: /\b(founders?|co-founders?|owners?|ceos?)\b/,
    facetLabel: 'Founders and owners',
    roleFilter: 'Founder',
    seniorityFilter: 'VP',
  },
];

const LOCATION_HINTS = [
  { pattern: /\bamsterdam\b/, country: 'Netherlands', city: 'Amsterdam' },
  { pattern: /\bcopenhagen\b/, country: 'Denmark', city: 'Copenhagen' },
  { pattern: /\bstockholm\b/, country: 'Sweden', city: 'Stockholm' },
  { pattern: /\boslo\b/, country: 'Norway', city: 'Oslo' },
  { pattern: /\bdenmark\b/, country: 'Denmark' },
  { pattern: /\bsweden\b/, country: 'Sweden' },
  { pattern: /\bnorway\b/, country: 'Norway' },
  { pattern: /\bnetherlands\b|\bholland\b/, country: 'Netherlands' },
  { pattern: /\bgermany\b/, country: 'Germany' },
  { pattern: /\buk\b|\bunited kingdom\b|\bengland\b/, country: 'United Kingdom' },
  { pattern: /\busa\b|\bunited states\b|\bu\.?s\.?a?\.?\b/, country: 'United States' },
];

const INDUSTRY_HINTS = [
  { pattern: /\b(dentist|dentists|dental|dental clinic|dental clinics)\b/, value: DENTIST_INDUSTRY_VALUE },
  { pattern: /\b(saas|software|platform)\b/, value: 'SaaS' },
  { pattern: /\b(agency|marketing|creative|content)\b/, value: 'Marketing Agency' },
  { pattern: /\b(ecommerce|e-commerce|shopify|dtc|retail)\b/, value: 'E-commerce' },
];

const COMPANY_OVERRIDE_AI_COLUMNS = [
  {
    label: 'Best contact roles',
    prompt: 'Name 2-3 job titles at this company that should own this pain and be viable first outreach targets.',
  },
  {
    label: 'Outreach thesis',
    prompt: 'Write a short company-specific outreach thesis grounded in industry, size, geography, and visible signals.',
  },
  {
    label: 'Fit score',
    prompt: 'Score this company from 1-10 for outreach fit and include one concise reason tied to row evidence.',
  },
];

const PEOPLE_OVERRIDE_AI_COLUMNS = [
  {
    label: 'Pain points',
    prompt: 'Infer 2-3 concrete pains this contact likely owns based on title, company, geography, and visible signals.',
  },
  {
    label: 'Opening line',
    prompt: 'Write a short personalized first line for outreach, grounded in one specific signal from the row.',
  },
  {
    label: 'Fit score',
    prompt: 'Score fit from 1-10 using role relevance, company fit, geography, and available intent signals.',
  },
];

export function getBusinessType(id) {
  return BUSINESS_TYPES.find((business) => business.id === id) || FALLBACK_BUSINESS;
}

export function getGoal(id) {
  return GOALS.find((goal) => goal.id === id) || FALLBACK_GOAL;
}

export function buildIcpFromSetup(setup) {
  const business = getBusinessType(setup.businessTypeId);
  const goal = getGoal(setup.goalId);
  const businessDescription = String(setup.businessDescription || '').trim();
  const inferred = inferDescriptionSignals(businessDescription);
  const searchType = resolveSearchType(goal.searchType, inferred);
  const entityLabel = searchType === 'companies' ? 'Companies' : 'People';
  const roleProfile = resolveRoleProfile({ goal, inferred, searchType });
  const preferInferredCopy = shouldPreferInferredCopy({ goal, inferred, searchType });

  const customBusinessLabel = business.id === 'other' && businessDescription
    ? summarizeBusinessDescription(businessDescription)
    : business.label;
  const industryValue = resolveIndustryValue({ business, customBusinessLabel, inferred });
  const businessForCopy = {
    ...business,
    label: customBusinessLabel,
  };

  const filters = buildSuggestedFilters({
    business,
    goal,
    searchType,
    inferred,
    roleProfile,
    industryValue,
  });
  const geographyValue = buildGeographyFacetValue({ filters, inferred });
  const headline = buildHeadline({
    goal,
    business: businessForCopy,
    searchType,
    industryValue,
    geographyValue,
    roleProfile,
    preferInferredCopy,
  });

  return {
    headline,
    summary: buildSummary({
      setup,
      business: businessForCopy,
      goal,
      businessDescription,
      entityLabel,
      searchType,
    }),
    searchType,
    setup: {
      forWho: setup.forWho,
      businessTypeId: business.id,
      businessDescription,
      goalId: goal.id,
    },
    facets: [
      { key: 'entity', label: 'Searching for', value: entityLabel },
      { key: 'roles', label: 'Roles', value: roleProfile.rolesLabel },
      { key: 'industry', label: 'Industry', value: industryValue },
      { key: 'geography', label: 'Geography', value: geographyValue },
      { key: 'companySize', label: 'Company size', value: COMPANY_SIZE_FILTERS_ENABLED ? business.sizeLabel : 'Temporarily disabled' },
      { key: 'signals', label: 'Signals', value: SIGNAL_FILTERS_ENABLED ? (searchType === 'companies' ? 'Actively hiring, visible growth signals' : goal.signals) : 'Temporarily disabled' },
    ],
    suggestedFilters: filters,
    suggestedAiColumns: resolveAiColumns({ goal, searchType }),
  };
}

export function updateIcpFacet(icp, facetKey, value) {
  return {
    ...icp,
    facets: (icp.facets || []).map((facet) => (
      facet.key === facetKey ? { ...facet, value } : facet
    )),
  };
}

export function suggestedColumnLabels(icp) {
  return (icp?.suggestedAiColumns || []).map((column) => column.label);
}

export function suggestedColumnPromptMap(icp) {
  return (icp?.suggestedAiColumns || []).reduce((prompts, column) => {
    if (!column.label || !column.prompt) return prompts;
    return {
      ...prompts,
      [column.label.trim().toLowerCase()]: column.prompt,
    };
  }, {});
}

function resolveSearchType(goalSearchType, inferred) {
  if (inferred.searchType) return inferred.searchType;
  return goalSearchType;
}

function resolveRoleProfile({ goal, inferred, searchType }) {
  if (searchType === 'companies') {
    return {
      rolesLabel: 'Company-level search (Able can enrich people after this)',
      roleFilter: null,
      seniorityFilter: null,
    };
  }

  if (inferred.roleHint) {
    return {
      rolesLabel: inferred.roleHint.facetLabel,
      roleFilter: inferred.roleHint.roleFilter,
      seniorityFilter: inferred.roleHint.seniorityFilter,
    };
  }

  return {
    rolesLabel: goal.roles,
    roleFilter: goal.roleFilter,
    seniorityFilter: goal.seniorityFilter,
  };
}

function resolveIndustryValue({ business, customBusinessLabel, inferred }) {
  if (inferred.industry) return canonicalizeIndustryValue(inferred.industry);
  if (business.id === 'other') return canonicalizeIndustryValue(customBusinessLabel);
  return canonicalizeIndustryValue(business.industry);
}

function resolveAiColumns({ goal, searchType }) {
  if (goal.searchType === searchType) return goal.aiColumns;
  return searchType === 'companies' ? COMPANY_OVERRIDE_AI_COLUMNS : PEOPLE_OVERRIDE_AI_COLUMNS;
}

function buildHeadline({
  goal,
  business,
  searchType,
  industryValue,
  geographyValue,
  roleProfile,
  preferInferredCopy,
}) {
  if (!preferInferredCopy) {
    return goal.headlineTemplate({ business });
  }

  if (searchType === 'companies') {
    if (industryValue && geographyValue) return `${industryValue} in ${geographyValue}`;
    if (industryValue) return `${industryValue} companies`;
    if (geographyValue) return `Companies in ${geographyValue}`;
    return goal.headlineTemplate({ business });
  }

  if (roleProfile.rolesLabel && industryValue && geographyValue) {
    return `${roleProfile.rolesLabel} at ${industryValue} in ${geographyValue}`;
  }
  if (roleProfile.rolesLabel && geographyValue) {
    return `${roleProfile.rolesLabel} in ${geographyValue}`;
  }
  return goal.headlineTemplate({ business });
}

function shouldPreferInferredCopy({ goal, inferred, searchType }) {
  return (
    searchType !== goal.searchType
    || Boolean(inferred.industry)
    || Boolean(inferred.city)
    || inferred.countries.length > 0
    || Boolean(inferred.roleHint)
  );
}

function buildSummary({ setup, business, goal, businessDescription, entityLabel, searchType }) {
  const owner = setup.forWho === 'client' ? 'your client' : 'your business';
  const problem = businessDescription || business.defaultProblem;
  const target = searchType === 'companies'
    ? 'companies and businesses'
    : entityLabel.toLowerCase();

  return `Best-fit ${target} for ${owner}: ${business.label.toLowerCase()}. Able focused on ${problem}, then translated that into filters and AI columns you can act on.`;
}

function buildSuggestedFilters({
  business,
  goal,
  searchType,
  inferred,
  roleProfile,
  industryValue,
}) {
  const countries = inferred.countries.length > 0 ? inferred.countries : DEFAULT_GEOGRAPHY;
  const filters = [
    {
      field: 'country',
      op: 'in',
      value: countries,
      label: `Geography: ${countries.join(', ')}`,
      source: 'ai',
    },
  ];

  if (inferred.city) {
    filters.push({
      field: 'city',
      op: 'in',
      value: [inferred.city],
      label: `City: ${inferred.city}`,
      source: 'ai',
    });
  }

  if (industryValue && industryValue.toLowerCase() !== 'custom') {
    filters.push({
      field: 'industry',
      op: 'contains',
      value: industryValue,
      label: `Industry: ${industryValue}`,
      source: 'ai',
    });
  }

  if (COMPANY_SIZE_FILTERS_ENABLED && business.sizeRanges.length > 0) {
    filters.push({
      field: searchType === 'companies' ? 'employeeRange' : 'companySize',
      op: 'in',
      value: business.sizeRanges,
      label: `Size: ${business.sizeLabel}`,
      source: 'ai',
    });
  }

  if (searchType === 'people') {
    if (roleProfile.seniorityFilter) {
      filters.push({
        field: 'seniority',
        op: 'equals',
        value: roleProfile.seniorityFilter,
        label: `Seniority: ${roleProfile.seniorityFilter}`,
        source: 'ai',
      });
    }
    if (roleProfile.roleFilter) {
      filters.push({
        field: 'role',
        op: 'contains',
        value: roleProfile.roleFilter,
        label: `Role: ${roleProfile.rolesLabel}`,
        source: 'ai',
      });
    }
    if (SIGNAL_FILTERS_ENABLED) {
      filters.push({
        field: 'insights',
        op: 'hasInsight',
        value: 'Active on LinkedIn',
        label: 'Signal: Active on LinkedIn',
        source: 'ai',
      });
    }
  } else if (SIGNAL_FILTERS_ENABLED) {
    filters.push({
      field: 'insights',
      op: 'hasInsight',
      value: 'Actively hiring',
      label: 'Signal: Actively hiring',
      source: 'ai',
    });
  }

  if (SIGNAL_FILTERS_ENABLED && HIRING_INTENT_REGEX.test(inferred.text)) {
    filters.push({
      field: 'insights',
      op: 'hasInsight',
      value: searchType === 'companies' ? 'Actively hiring' : 'Recent hiring',
      label: searchType === 'companies' ? 'Signal: Actively hiring' : 'Signal: Recent hiring',
      source: 'ai',
    });
  }

  return dedupeFilters(filters);
}

function buildGeographyFacetValue({ filters, inferred }) {
  const countryFilter = filters.find((filter) => filter.field === 'country');
  const countries = Array.isArray(countryFilter?.value) ? countryFilter.value : DEFAULT_GEOGRAPHY;
  if (inferred.city && countries.length > 0) {
    return `${inferred.city}, ${countries[0]}`;
  }
  return countries.join(', ');
}

function inferDescriptionSignals(description) {
  const text = String(description || '').toLowerCase();
  if (!text) {
    return {
      text: '',
      countries: [],
      city: null,
      industry: null,
      roleHint: null,
      searchType: null,
    };
  }

  const countries = [];
  let city = null;

  LOCATION_HINTS.forEach((hint) => {
    if (!hint.pattern.test(text)) return;
    if (!countries.includes(hint.country)) countries.push(hint.country);
    if (!city && hint.city) city = hint.city;
  });

  const industryHint = INDUSTRY_HINTS.find((hint) => hint.pattern.test(text)) || null;
  const roleHint = ROLE_HINTS.find((hint) => hint.pattern.test(text)) || null;
  const hasPeopleIntent = Boolean(roleHint) || PEOPLE_INTENT_REGEX.test(text);
  const hasCompanyIntent = Boolean(industryHint) || COMPANY_INTENT_REGEX.test(text);

  let searchType = null;
  if (hasPeopleIntent) {
    searchType = 'people';
  } else if (hasCompanyIntent) {
    searchType = 'companies';
  }

  return {
    text,
    countries,
    city,
    industry: industryHint?.value || null,
    roleHint,
    searchType,
  };
}

function dedupeFilters(filters) {
  const seen = new Set();
  const deduped = [];

  for (const filter of filters) {
    const key = `${filter.field}:${filter.op}:${JSON.stringify(filter.value)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(filter);
  }

  return deduped;
}

function summarizeBusinessDescription(description) {
  const trimmed = description.trim().replace(/\s+/g, ' ');
  if (trimmed.length <= 58) return trimmed;
  return `${trimmed.slice(0, 57).trim()}...`;
}
